import {useCallback, useEffect, useMemo, useState} from "react";
import {
    Box,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography
} from "@mui/material";
import {KeyboardArrowDown, KeyboardArrowRight} from "@mui/icons-material";
import {formatCurrency, formatHierarchyNames, formatNumber, formatPercent} from "../utils/formatters";

const ROW_BG = {
    0: undefined,
    1: '#F5F7FB',
    2: '#FAFAFA'
};

const ROW_INDENT = {
    0: 16,
    1: 56,
    2: 96
};

const HEADER_BASE_STYLE = {
    border: '1px solid rgba(224, 224, 224, 1)',
    borderRight: 'none',
    position: 'sticky',
    top: 0,
    zIndex: 2
};

const HEADER_ROWSPAN_STYLE = {
    ...HEADER_BASE_STYLE,
    zIndex: 3
};

const HEADER_COLORS = {
    dimension: '#D9D9D9',
    current: '#DAF2D0',
    prior: '#A6C9EC',
    delta: '#BFBFBF'
};

const METRIC_COL_WIDTH = '130px';

const METRIC_CELL_STYLE = {
    width: METRIC_COL_WIDTH,
    minWidth: METRIC_COL_WIDTH,
    maxWidth: METRIC_COL_WIDTH,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    px: 0.5,
    boxSizing: 'border-box'
};

const NAME_COLUMN_STYLE = {minWidth: '200px', width: 'auto', maxWidth: 'none'};

const DIMENSION_COL_STYLES = {
    hierarchy: {width: '180px', minWidth: '180px', maxWidth: '180px'},
    number: {width: '150px', minWidth: '150px', maxWidth: '150px'}
};

const DIMENSION_CELL_STYLE = {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    verticalAlign: 'top',
    py: 1.5,
    lineHeight: 1.45
};

const DELTA_POSITIVE_COLOR = '#4CAF50';
const DELTA_NEGATIVE_COLOR = '#F44336';

const isExpandableGroupBy = (groupBy) => groupBy === 'hq_account_id' || groupBy === 'account_id';

const hasPreloadedChildren = (row) => Array.isArray(row?.products) && row.products.length > 0;

const isRowExpandable = (groupBy, row) => isExpandableGroupBy(groupBy) || hasPreloadedChildren(row);

const getDimensionColumns = (groupBy) => {
    if (groupBy === 'sales_person_id') {
        return {showHierarchy: false, showNumber: false, dimensionCount: 1};
    }
    return {showHierarchy: true, showNumber: true, dimensionCount: 3};
};

const getTableMinWidth = (dimensionConfig, metricsWidth) => {
    let minWidth = metricsWidth + 200;
    if (dimensionConfig.showHierarchy) {
        minWidth += parseInt(DIMENSION_COL_STYLES.hierarchy.width, 10);
    }
    if (dimensionConfig.showNumber) {
        minWidth += parseInt(DIMENSION_COL_STYLES.number.width, 10);
    }
    return minWidth;
};

const formatMetricValue = (value, valueType) => {
    if (value === null || value === undefined) {
        return '—';
    }
    if (valueType === 'percent') {
        return formatPercent(value) ?? '0.0%';
    }
    if (valueType === 'count') {
        return formatNumber(value) ?? '0';
    }
    return formatCurrency(value) ?? '$0';
};

const formatDeltaValue = (value, valueType) => {
    if (value === null || value === undefined) {
        return '—';
    }
    if (value === 0) {
        return formatMetricValue(0, valueType) ?? '0';
    }
    const formatted = formatMetricValue(Math.abs(value), valueType);
    if (!formatted) {
        return '—';
    }
    return value > 0 ? `+${formatted}` : `-${formatted}`;
};

const getDeltaColor = (value) => {
    if (value === null || value === undefined || value === 0) {
        return undefined;
    }
    return value > 0 ? DELTA_POSITIVE_COLOR : DELTA_NEGATIVE_COLOR;
};

const renderHierarchyText = (hierarchyNames) => {
    if (hierarchyNames == null) {
        return '—';
    }
    return formatHierarchyNames(hierarchyNames, ' / ');
};

const renderDimensionText = (value, fontWeight = 400) => (
    <Typography variant="body2" fontWeight={fontWeight} sx={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
        {value || '—'}
    </Typography>
);

const getSortValue = (row, sortKey, calcTypes) => {
    if (sortKey === 'name') {
        return row.name || '';
    }
    if (sortKey === 'hierarchy_names') {
        return formatHierarchyNames(row.hierarchy_names, ' / ');
    }
    if (sortKey === 'number') {
        return row.number ?? '';
    }

    const metricMatch = sortKey.match(/^(.+)_(current|prior|change)$/);
    if (metricMatch) {
        const [, calcType, period] = metricMatch;
        return row[calcType]?.[period] ?? 0;
    }

    return 0;
};

const sortRows = (rows, sortKey, sortDir, calcTypes) => {
    if (!sortKey) {
        return rows;
    }

    const direction = sortDir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
        const aVal = getSortValue(a, sortKey, calcTypes);
        const bVal = getSortValue(b, sortKey, calcTypes);

        if (typeof aVal === 'string' || typeof bVal === 'string') {
            return String(aVal).localeCompare(String(bVal)) * direction;
        }
        if (aVal === bVal) {
            return (a.name || '').localeCompare(b.name || '') * direction;
        }
        return (aVal - bVal) * direction;
    });
};

const renderMetricCell = (row, calcType, period, calcConfigs) => {
    const metric = row[calcType] || {current: null, prior: null, change: null};
    const valueType = calcConfigs?.[calcType]?.valueType;
    const value = metric[period];
    const isDelta = period === 'change';
    const displayValue = isDelta
        ? formatDeltaValue(value, valueType)
        : formatMetricValue(value, valueType);
    const color = isDelta ? getDeltaColor(value) : undefined;

    return (
        <TableCell key={`${row.id}-${calcType}-${period}`} align="right" sx={METRIC_CELL_STYLE}>
            <Typography
                component="span"
                variant="body2"
                noWrap
                title={typeof displayValue === 'string' ? displayValue : undefined}
                sx={{color, fontWeight: isDelta && color ? 600 : undefined}}
            >
                {displayValue}
            </Typography>
        </TableCell>
    );
};

const MetricCells = ({row, calcTypes, calcConfigs}) => ['current', 'prior', 'change'].flatMap((period) =>
    calcTypes.map((calcType) => renderMetricCell(row, calcType, period, calcConfigs))
);

const TreeRow = ({
    row,
    rowKey,
    depth,
    childrenByParentId,
    expandedIds,
    loadingRowIds,
    onToggleExpand,
    calcTypes,
    calcConfigs,
    dimensionConfig,
    groupBy,
    sortKey,
    sortDir
}) => {
    const childData = childrenByParentId.get(rowKey);
    const children = useMemo(
        () => sortRows(childData?.breakdown || [], sortKey, sortDir, calcTypes),
        [childData, sortKey, sortDir, calcTypes]
    );
    const childGroupBy = childData?.groupBy;
    const expanded = expandedIds.has(rowKey);
    const isLoading = loadingRowIds.has(rowKey);
    const isExpandable = isRowExpandable(groupBy, row);
    const rowDimensionConfig = dimensionConfig || getDimensionColumns(groupBy);

    const handleClick = () => {
        if (isExpandable) {
            onToggleExpand(row, rowKey, groupBy);
        }
    };

    return (
        <>
            <TableRow
                hover={isExpandable}
                onClick={handleClick}
                sx={{
                    cursor: isExpandable ? 'pointer' : 'default',
                    backgroundColor: ROW_BG[depth] || ROW_BG[2],
                    '& td': {verticalAlign: 'top'}
                }}
            >
                <TableCell sx={{...NAME_COLUMN_STYLE, ...DIMENSION_CELL_STYLE, paddingLeft: `${ROW_INDENT[depth] || ROW_INDENT[2]}px`}}>
                    <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                        {isExpandable ? (
                            <IconButton size="small" tabIndex={-1} disableRipple sx={{p: 0.5, mt: -0.25}}>
                                {isLoading
                                    ? <CircularProgress size={14}/>
                                    : expanded
                                        ? <KeyboardArrowDown fontSize="small"/>
                                        : <KeyboardArrowRight fontSize="small"/>}
                            </IconButton>
                        ) : (
                            <Box sx={{width: 34, flexShrink: 0}}/>
                        )}
                        <Box sx={{minWidth: 0, flex: 1}}>
                            {renderDimensionText(row.name, depth === 0 ? 600 : depth === 1 ? 500 : 400)}
                        </Box>
                    </Box>
                </TableCell>
                {rowDimensionConfig.showHierarchy && (
                    <TableCell sx={{...DIMENSION_COL_STYLES.hierarchy, ...DIMENSION_CELL_STYLE}}>
                        {renderDimensionText(renderHierarchyText(row.hierarchy_names))}
                    </TableCell>
                )}
                {rowDimensionConfig.showNumber && (
                    <TableCell sx={{...DIMENSION_COL_STYLES.number, ...DIMENSION_CELL_STYLE}}>
                        {renderDimensionText(row.number != null ? String(row.number) : null)}
                    </TableCell>
                )}
                <MetricCells row={row} calcTypes={calcTypes} calcConfigs={calcConfigs}/>
            </TableRow>
            {expanded && childGroupBy && children.map((child) => (
                <TreeRow
                    key={`${rowKey}:${child.id}`}
                    row={child}
                    rowKey={`${rowKey}:${child.id}`}
                    depth={depth + 1}
                    childrenByParentId={childrenByParentId}
                    expandedIds={expandedIds}
                    loadingRowIds={loadingRowIds}
                    onToggleExpand={onToggleExpand}
                    calcTypes={calcTypes}
                    calcConfigs={calcConfigs}
                    dimensionConfig={getDimensionColumns(childGroupBy)}
                    groupBy={childGroupBy}
                    sortKey={sortKey}
                    sortDir={sortDir}
                />
            ))}
        </>
    );
};

export const MeasureDetailTable = ({
    breakdown = [],
    calcConfigs = {},
    groupBy = 'hq_account_id',
    loading,
    onLoadChildren,
    title
}) => {
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [loadingRowIds, setLoadingRowIds] = useState(new Set());
    const [childrenByParentId, setChildrenByParentId] = useState(new Map());
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState('asc');

    const calcTypes = useMemo(() => Object.keys(calcConfigs), [calcConfigs]);
    const topLevelRows = useMemo(() => breakdown, [breakdown]);
    const dimensionConfig = useMemo(() => getDimensionColumns(groupBy), [groupBy]);

    const topLevelRowSignature = useMemo(
        () => topLevelRows.map((row) => row.id).join('|'),
        [topLevelRows]
    );

    const sortedTopLevelRows = useMemo(
        () => sortRows(topLevelRows, sortKey, sortDir, calcTypes),
        [topLevelRows, sortKey, sortDir, calcTypes]
    );

    const paginatedTopLevelRows = useMemo(
        () => sortedTopLevelRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedTopLevelRows, page, rowsPerPage]
    );

    const metricColumnCount = calcTypes.length * 3;
    const totalColumns = dimensionConfig.dimensionCount + metricColumnCount;
    const metricColWidth = parseInt(METRIC_COL_WIDTH, 10);
    const metricsWidth = metricColumnCount * metricColWidth;
    const tableMinWidth = getTableMinWidth(dimensionConfig, metricsWidth);

    useEffect(() => {
        setPage(0);
        setExpandedIds(new Set());
        setLoadingRowIds(new Set());
        setChildrenByParentId(new Map());
    }, [topLevelRowSignature, groupBy]);

    const toggleExpand = useCallback((row, rowKey, rowGroupBy) => {
        const childData = childrenByParentId.get(rowKey);
        const children = childData?.breakdown || [];
        const isExpanded = expandedIds.has(rowKey);

        if (!isExpanded && isRowExpandable(rowGroupBy, row) && children.length === 0) {
            if (hasPreloadedChildren(row)) {
                setChildrenByParentId((prev) => {
                    const next = new Map(prev);
                    next.set(rowKey, {
                        breakdown: row.products,
                        groupBy: 'product_id',
                    });
                    return next;
                });
                setExpandedIds((prev) => new Set(prev).add(rowKey));
                return;
            }

            if (onLoadChildren) {
                setLoadingRowIds((prev) => new Set(prev).add(rowKey));
                onLoadChildren(row, rowGroupBy, (result) => {
                    setChildrenByParentId((prev) => {
                        const next = new Map(prev);
                        next.set(rowKey, {
                            breakdown: result?.breakdown || [],
                            groupBy: result?.groupBy || null,
                        });
                        return next;
                    });
                    setLoadingRowIds((prev) => {
                        const next = new Set(prev);
                        next.delete(rowKey);
                        return next;
                    });
                    setExpandedIds((prev) => new Set(prev).add(rowKey));
                });
                return;
            }
        }

        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }
            return next;
        });
    }, [childrenByParentId, expandedIds, onLoadChildren]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
        setPage(0);
    };

    const tableTitle = title;

    const renderSortLabel = (label, field) => (
        <TableSortLabel
            active={sortKey === field}
            direction={sortKey === field ? sortDir : 'asc'}
            onClick={() => handleSort(field)}
        >
            {label}
        </TableSortLabel>
    );

    const renderMetricHeaders = (suffix, bg, periodKey) => calcTypes.map((calcType) => (
        <TableCell
            key={`${suffix}-${calcType}`}
            align="center"
            sx={{
                ...HEADER_BASE_STYLE,
                top: '37px',
                ...METRIC_CELL_STYLE,
                backgroundColor: bg,
                whiteSpace: 'pre-line',
                fontWeight: 600
            }}
        >
            {renderSortLabel(calcConfigs[calcType]?.name || calcType, `${calcType}_${periodKey}`)}
        </TableCell>
    ));

    return (
        <Box sx={{width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box'}}>
            {tableTitle && (
                <Box sx={{mb: 2}}>
                    <Typography variant="h6" color="primary">
                        {tableTitle}
                    </Typography>
                </Box>
            )}

            <TableContainer sx={{width: '100%', maxWidth: '100%', overflow: 'auto'}}>
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        tableLayout: 'fixed',
                        width: '100%',
                        minWidth: tableMinWidth
                    }}
                >
                    <colgroup>
                        <col/>
                        {dimensionConfig.showHierarchy && (
                            <col style={{width: DIMENSION_COL_STYLES.hierarchy.width}}/>
                        )}
                        {dimensionConfig.showNumber && (
                            <col style={{width: DIMENSION_COL_STYLES.number.width}}/>
                        )}
                        {Array.from({length: metricColumnCount}, (_, index) => (
                            <col key={index} style={{width: METRIC_COL_WIDTH}}/>
                        ))}
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                rowSpan={2}
                                align="center"
                                sx={{...HEADER_ROWSPAN_STYLE, ...NAME_COLUMN_STYLE, backgroundColor: HEADER_COLORS.dimension, fontWeight: 600}}
                            >
                                {renderSortLabel('Name', 'name')}
                            </TableCell>
                            {dimensionConfig.showHierarchy && (
                                <TableCell
                                    rowSpan={2}
                                    align="center"
                                    sx={{...HEADER_ROWSPAN_STYLE, ...DIMENSION_COL_STYLES.hierarchy, backgroundColor: HEADER_COLORS.dimension, fontWeight: 600}}
                                >
                                    {renderSortLabel('Hierarchy', 'hierarchy_names')}
                                </TableCell>
                            )}
                            {dimensionConfig.showNumber && (
                                <TableCell
                                    rowSpan={2}
                                    align="center"
                                    sx={{...HEADER_ROWSPAN_STYLE, ...DIMENSION_COL_STYLES.number, backgroundColor: HEADER_COLORS.dimension, fontWeight: 600}}
                                >
                                    {renderSortLabel('Number', 'number')}
                                </TableCell>
                            )}
                            <TableCell
                                align="center"
                                colSpan={calcTypes.length}
                                sx={{...HEADER_BASE_STYLE, borderBottom: 'none', backgroundColor: HEADER_COLORS.current, fontWeight: 600}}
                            >
                                Current
                            </TableCell>
                            <TableCell
                                align="center"
                                colSpan={calcTypes.length}
                                sx={{...HEADER_BASE_STYLE, borderBottom: 'none', backgroundColor: HEADER_COLORS.prior, fontWeight: 600}}
                            >
                                Prior
                            </TableCell>
                            <TableCell
                                align="center"
                                colSpan={calcTypes.length}
                                sx={{...HEADER_BASE_STYLE, borderBottom: 'none', backgroundColor: HEADER_COLORS.delta, fontWeight: 600}}
                            >
                                Delta
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            {renderMetricHeaders('current', HEADER_COLORS.current, 'current')}
                            {renderMetricHeaders('prior', HEADER_COLORS.prior, 'prior')}
                            {renderMetricHeaders('delta', HEADER_COLORS.delta, 'change')}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={totalColumns} sx={{textAlign: 'center', py: 4}}>
                                    <CircularProgress size={24}/>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && topLevelRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={totalColumns} sx={{textAlign: 'center', py: 4}}>
                                    <Typography color="textSecondary">No data available</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && paginatedTopLevelRows.map((row) => (
                            <TreeRow
                                key={row.id}
                                row={row}
                                rowKey={String(row.id)}
                                depth={0}
                                childrenByParentId={childrenByParentId}
                                expandedIds={expandedIds}
                                loadingRowIds={loadingRowIds}
                                onToggleExpand={toggleExpand}
                                calcTypes={calcTypes}
                                calcConfigs={calcConfigs}
                                dimensionConfig={dimensionConfig}
                                groupBy={groupBy}
                                sortKey={sortKey}
                                sortDir={sortDir}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {!loading && topLevelRows.length > 0 && (
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    count={topLevelRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            )}
        </Box>
    );
};
